import React, { useEffect, useState } from 'react'

// Stable object key order avoids highlighting JSON formatting as a change.
const stable = value => {
  if (Array.isArray(value)) return value.map(stable)
  if (value && typeof value === 'object') return Object.keys(value).sort().reduce((result, key) => {
    result[key] = stable(value[key])
    return result
  }, {})
  return value
}

export function diffLines(before, after) {
  const left = before === null ? [] : JSON.stringify(stable(before), null, 2).split('\n')
  const right = JSON.stringify(stable(after), null, 2).split('\n')
  // Bound memory for unusually large specification lists.
  const lengths = left.length * right.length <= 1000000
    ? Array.from({ length: left.length + 1 }, () => new Uint32Array(right.length + 1)) : null
  if (lengths) {
    for (let i = left.length - 1; i >= 0; i--) {
      for (let j = right.length - 1; j >= 0; j--) {
        lengths[i][j] = left[i] === right[j] ? lengths[i + 1][j + 1] + 1
          : Math.max(lengths[i + 1][j], lengths[i][j + 1])
      }
    }
  }
  const rows = []
  let i = 0
  let j = 0
  let removed = []
  let added = []
  const flush = () => {
    for (let k = 0; k < Math.max(removed.length, added.length); k++) {
      rows.push({ left: removed[k], right: added[k], changed: true })
    }
    removed = []
    added = []
  }
  while (i < left.length || j < right.length) {
    if (i < left.length && j < right.length && left[i] === right[j]) {
      flush()
      rows.push({ left: { number: i + 1, text: left[i++] }, right: { number: j + 1, text: right[j++] } })
    } else if (i < left.length && (j === right.length || !lengths || lengths[i + 1][j] >= lengths[i][j + 1])) {
      removed.push({ number: i + 1, text: left[i++] })
    } else {
      added.push({ number: j + 1, text: right[j++] })
    }
  }
  flush()
  return rows
}

// Keep three context lines next to changes; collapse only runs hiding at least three lines.
export function collapseLines(rows, expanded = {}) {
  const result = []
  let start = 0
  while (start < rows.length) {
    if (rows[start].changed) {
      result.push(rows[start++])
      continue
    }
    let end = start
    while (end < rows.length && !rows[end].changed) end++
    const hiddenStart = start === 0 ? start : Math.min(start + 3, end)
    const hiddenEnd = end === rows.length ? end : Math.max(hiddenStart, end - 3)
    if (hiddenEnd - hiddenStart >= 3 && !expanded[hiddenStart]) {
      result.push(...rows.slice(start, hiddenStart))
      result.push({ collapsed: hiddenEnd - hiddenStart, start: hiddenStart })
      result.push(...rows.slice(hiddenEnd, end))
    } else {
      result.push(...rows.slice(start, end))
    }
    start = end
  }
  return result
}

export default function JsonDiff({ before, after }) {
  const [expanded, setExpanded] = useState({})
  useEffect(() => { setExpanded({}) }, [before, after])
  const rows = collapseLines(diffLines(before, after), expanded)
  return <table className="filter-import-diff" aria-label="JSON changes">
    <colgroup><col style={{ width: 44 }} /><col /><col style={{ width: 44 }} /><col /></colgroup>
    <thead><tr><th colSpan="2">In database</th><th colSpan="2">After import</th></tr></thead>
    <tbody>{rows.map((row, index) => row.collapsed ? <tr key={`collapsed-${row.start}`}>
      <td colSpan="4" className="diff-collapsed">
        <button type="button" aria-expanded="false"
          onClick={() => setExpanded(previous => ({ ...previous, [row.start]: true }))}>
          ↕ Show {row.collapsed} unchanged lines
        </button>
      </td>
    </tr> : <tr key={index}>
      {['left', 'right'].map(side => {
        const line = row[side]
        const marker = row.changed && line ? (side === 'left' ? '−' : '+') : ' '
        const className = !line ? 'diff-empty' : row.changed ? (side === 'left' ? 'diff-removed' : 'diff-added') : ''
        return <React.Fragment key={side}>
          <td className={`diff-number ${className}`}>{line && line.number}</td>
          <td className={`diff-code ${className}`}><pre>{marker} {line && line.text}</pre></td>
        </React.Fragment>
      })}
    </tr>)}</tbody>
  </table>
}
